import { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import type { Control, UseFormGetValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { Button, ComboBox, FieldError, Input, Label, ListBox, ListBoxItem } from '@heroui/react'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection } from '@/components/ui/form-section'
import {
  DateFieldControl,
  NumberFieldControl,
  SelectFieldControl,
  TextAreaFieldControl,
} from '@/components/ui/form-fields'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import { fetchStockItems } from '../api'
import {
  inventoryKey,
  useAdjustmentActions,
  useInventoryLocations,
  useReasonCodes,
  useStockItems,
  useStockLevels,
} from '../hooks'
import type {
  AdjustmentKind,
  CreateAdjustmentBody,
  StockItem,
  StockItemQuery,
  StockLevel,
} from '../types'
import {
  adjustmentKindHints,
  adjustmentKindLabels,
  inOutletOrder,
  isCounted,
  locationLabel,
  quantity,
  reasonCategoriesOf,
  reasonCategoryLabels,
  variance,
} from './inventory-format'

/** Quantities take four decimals, as the service stores them; a unit cost six. */
const QUANTITY_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 4 }
const COST_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 6 }

/** What can be adjusted: the first page of stock items, as large as the API allows. */
const CANDIDATE_QUERY = { limit: PAGE_LIMIT_MAX, offset: 0 }

/** The batch stock read answers for at most this many items. */
const STOCK_READ_MAX = 50

/**
 * How many matches a typed search asks for. More than the recipe editor's ten:
 * the uncounted ones are dropped after the answer — the list cannot filter on
 * tracking — and the picker should still have a handful left to show.
 */
const MATCH_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 300

/** Items whose name, SKU or barcode hold the text — the box's search, or a scanned code. */
function matchQuery(search: string): StockItemQuery {
  return { limit: MATCH_LIMIT, offset: 0, search }
}

/** A scanned or typed code against an item: its barcode or SKU, whole, ignoring case. */
function isCodeOf(item: StockItem, code: string): boolean {
  const wanted = code.toLowerCase()
  return item.barcode?.toLowerCase() === wanted || item.sku.toLowerCase() === wanted
}

const KINDS = ['adjustment', 'wastage', 'opening'] as const satisfies readonly AdjustmentKind[]

const lineSchema = z.object({
  itemId: z.string().min(1, 'Choose an item.'),
  qty: z.number().optional(),
  reasonCodeId: z.string().optional(),
  unitCost: z.number().optional(),
})

/** The rules that need nothing but the form; the cost rule needs the items, see `submit`. */
const schema = z
  .object({
    kind: z.enum(KINDS),
    locationId: z.string().min(1, 'Choose a location.'),
    /** `yyyy-mm-dd`; empty or today means now. */
    occurredOn: z.string().optional(),
    note: z.string().max(2000).optional(),
    lines: z.array(lineSchema).min(1, 'Add at least one item.'),
  })
  .superRefine((values, context) => {
    values.lines.forEach((line, index) => {
      const at = (field: string, message: string) =>
        context.addIssue({ code: 'custom', path: ['lines', index, field], message })

      if (line.qty === undefined) at('qty', 'Enter a quantity.')
      else if (values.kind === 'adjustment' && line.qty === 0)
        at('qty', 'Enter a change other than 0.')
      else if (values.kind !== 'adjustment' && line.qty <= 0) at('qty', 'Enter more than 0.')

      if (values.kind !== 'opening' && !line.reasonCodeId) at('reasonCodeId', 'Choose a reason.')
      if (values.kind === 'opening' && line.unitCost === undefined) {
        at('unitCost', 'Enter what one unit cost.')
      }
    })

    const ids = values.lines.map((line) => line.itemId).filter(Boolean)
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: 'custom',
        path: ['lines'],
        message: 'Each item can only be listed once.',
      })
    }
  })

type FormValues = z.infer<typeof schema>

const NEW_LINE: FormValues['lines'][number] = { itemId: '' }

/** Today as the date field spells it, in the browser's own time zone. */
function today(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** An item with no cost yet: found stock of it needs one typed in, which becomes its cost. */
function hasNoCost(item: StockItem | undefined): boolean {
  return !!item && !(item.currentAvgCost > 0) && !(item.defaultUnitCost > 0)
}

/** Whether the line asks for a unit cost: always on an opening, on found stock of a costless item. */
function needsCost(kind: AdjustmentKind, item: StockItem | undefined, qty: number | undefined) {
  return kind === 'opening' || (kind === 'adjustment' && (qty ?? 0) > 0 && hasNoCost(item))
}

interface AdjustmentFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSaved: () => void
}

/**
 * A new adjustment, wastage or opening balance at one location, saved as a draft.
 *
 * Quantities are in each item's own unit. An adjustment's are signed — a minus
 * takes stock away; a wastage's are what was thrown out; an opening's what is on
 * the shelf on day one, with what one unit cost. Every line but an opening's
 * says why, from the reasons its kind allows.
 *
 * Saving moves no stock: the draft is submitted from the list.
 */
export function AdjustmentFormDialog({ isOpen, onOpenChange, onSaved }: AdjustmentFormDialogProps) {
  const { create } = useAdjustmentActions()

  /* One id for the document, however often the save is retried: the service
     answers a second copy of it with the first. */
  const [clientDocId] = useState(() => crypto.randomUUID())
  /* One idempotency key per distinct body, as on the other create forms. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const { control, handleSubmit, formState, setError, getValues } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      kind: 'adjustment',
      locationId: '',
      occurredOn: today(),
      note: '',
      lines: [NEW_LINE],
    },
  })

  const lines = useFieldArray({ control, name: 'lines' })
  const kind = useWatch({ control, name: 'kind' })
  const locationId = useWatch({ control, name: 'locationId' })
  const values = useWatch({ control, name: 'lines' })

  const { locations } = useInventoryLocations()
  const locationOptions = useMemo(
    () =>
      inOutletOrder(locations).map((location) => ({
        value: location.id,
        label: locationLabel(location),
      })),
    [locations],
  )

  /* Only counted items hold stock; the service refuses the others. The first
     page is what the picker offers before anything is typed. */
  const { items: candidates } = useStockItems(CANDIDATE_QUERY)
  const items = useMemo(() => candidates.filter(isCounted), [candidates])

  /* Items found by a search or a scan are on no page held here, so each one
     picked is kept — its unit, cost and stock are what the line reads. */
  const [picked, setPicked] = useState<ReadonlyMap<string, StockItem>>(new Map())
  const known = useMemo(
    () => new Map([...items.map((item) => [item.id, item] as const), ...picked]),
    [items, picked],
  )
  const remember = (item: StockItem) => setPicked((before) => new Map(before).set(item.id, item))

  /* The reasons a picker offers: active, not system, in the kind's categories. */
  const { reasons } = useReasonCodes()
  const reasonOptions = useMemo(
    () =>
      reasons
        .filter(
          (reason) =>
            !reason.system &&
            reason.status === 'active' &&
            reasonCategoriesOf[kind].includes(reason.category),
        )
        .map((reason) => ({
          value: reason.id,
          label: `${reason.name} · ${reasonCategoryLabels[reason.category]}`,
        })),
    [reasons, kind],
  )

  /* What each chosen item holds at the location now, so a line shows what it
     will change before it is saved. */
  const chosenIds = useMemo(
    () => [...new Set(values.map((line) => line.itemId).filter(Boolean))].slice(0, STOCK_READ_MAX),
    [values],
  )
  const { levels } = useStockLevels(chosenIds, locationId || null)

  const submit = handleSubmit(async (form) => {
    // the one rule the schema cannot see: found stock of an item with no cost yet
    let missingCost = false
    form.lines.forEach((line, index) => {
      const item = known.get(line.itemId)
      if (needsCost(form.kind, item, line.qty) && line.unitCost === undefined) {
        setError(`lines.${index}.unitCost`, {
          message: 'This item has no cost yet: enter what one unit cost.',
        })
        missingCost = true
      }
    })
    if (missingCost) return

    const body: CreateAdjustmentBody = {
      kind: form.kind,
      locationId: form.locationId,
      clientDocId,
      // a day in the past is taken at noon; today is now
      ...(form.occurredOn &&
        form.occurredOn !== today() && {
          occurredAt: new Date(`${form.occurredOn}T12:00:00`).toISOString(),
        }),
      ...(form.note?.trim() && { note: form.note.trim() }),
      lines: form.lines.map((line) => {
        const item = known.get(line.itemId)
        return {
          itemId: line.itemId,
          qty: line.qty as number,
          ...(form.kind !== 'opening' && { reasonCodeId: line.reasonCodeId }),
          ...(needsCost(form.kind, item, line.qty) && { unitCost: line.unitCost }),
        }
      }),
    }

    const serialised = JSON.stringify(body)
    const next =
      attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
    setAttempt(next)

    await create.mutateAsync({ body, idempotencyKey: next.key })
    onSaved()
  })

  const listError = formState.errors.lines?.root?.message ?? formState.errors.lines?.message

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="New adjustment"
      description="Saved as a draft. Stock only changes once it is submitted (and approved, over your threshold)."
      submitLabel="Save draft"
      onSubmit={submit}
      isBusy={create.isPending}
      error={create.error}
      errorFallback="Could not save this adjustment."
      dialogClassName="max-w-4xl"
      scroll="inside"
    >
      <FormSection title="The adjustment" description={adjustmentKindHints[kind]}>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectFieldControl
            control={control}
            name="kind"
            label="Kind"
            isRequired
            options={KINDS.map((value) => ({ value, label: adjustmentKindLabels[value] }))}
          />
          <SelectFieldControl
            control={control}
            name="locationId"
            label="Location"
            placeholder="Choose…"
            isRequired
            options={locationOptions}
          />
          <DateFieldControl
            control={control}
            name="occurredOn"
            label="Happened on"
            max={today()}
            description="An earlier day is recorded at noon."
          />
        </div>
        <TextAreaFieldControl control={control} name="note" label="Note" placeholder="Optional" />
      </FormSection>

      <FormSection
        title="Items"
        description={
          kind === 'adjustment'
            ? 'How much each item goes up or down by, in its own unit — a minus takes stock away.'
            : kind === 'wastage'
              ? 'How much of each item was thrown out or used, in its own unit.'
              : 'What is on the shelf, in each item’s own unit, and what one unit cost. Only for items with no stock history at this location.'
        }
        action={
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-8 rounded-full border px-3 text-xs"
            onPress={() => lines.append(NEW_LINE)}
          >
            <PlusIcon width={14} height={14} />
            Add item
          </Button>
        }
        alert={
          listError && (
            <p role="alert" className="text-danger mt-2 text-xs">
              {listError}
            </p>
          )
        }
      >
        <div className="flex flex-col gap-3">
          {lines.fields.map((field, index) => {
            const line = values[index]
            const item = line?.itemId ? known.get(line.itemId) : undefined
            const showCost = needsCost(kind, item, line?.qty)

            return (
              <div
                key={field.id}
                className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1fr)_150px_minmax(0,1fr)_auto]"
              >
                <ItemField
                  index={index}
                  control={control}
                  getValues={getValues}
                  firstPage={items}
                  selected={item}
                  takenIds={values
                    .filter((_, other) => other !== index)
                    .map((other) => other?.itemId)
                    .filter((id): id is string => Boolean(id))}
                  onPick={remember}
                />
                <NumberFieldControl
                  control={control}
                  name={`lines.${index}.qty` as const}
                  label={`${kind === 'adjustment' ? 'Change' : 'Quantity'}${item ? ` (${item.unitOfMeasure})` : ''}`}
                  minValue={kind === 'adjustment' ? undefined : 0}
                  formatOptions={QUANTITY_FORMAT}
                  isRequired
                />
                {kind === 'opening' ? (
                  <span aria-hidden className="hidden sm:block" />
                ) : (
                  <SelectFieldControl
                    control={control}
                    name={`lines.${index}.reasonCodeId` as const}
                    label="Reason"
                    placeholder="Choose…"
                    isRequired
                    options={reasonOptions}
                  />
                )}
                <Button
                  variant="ghost"
                  aria-label="Remove item"
                  isDisabled={lines.fields.length === 1}
                  onPress={() => lines.remove(index)}
                  className="text-muted hover:text-danger size-11 self-end rounded-full"
                >
                  <TrashIcon width={16} height={16} />
                </Button>

                {showCost && (
                  <div className="sm:col-span-2">
                    <NumberFieldControl
                      control={control}
                      name={`lines.${index}.unitCost` as const}
                      label={`Cost of one ${item?.unitOfMeasure ?? 'unit'}`}
                      minValue={0}
                      formatOptions={COST_FORMAT}
                      isRequired
                      description={
                        kind === 'opening'
                          ? 'Becomes the item’s average cost.'
                          : 'The item has no cost yet; this becomes its cost.'
                      }
                    />
                  </div>
                )}

                {item && locationId && (
                  <p className="text-muted text-xs tabular-nums sm:col-span-4">
                    {onHandLine(levels.get(item.id), kind, line?.qty, item.unitOfMeasure)}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </FormSection>
    </FormModal>
  )
}

interface ItemFieldProps {
  index: number
  control: Control<FormValues>
  getValues: UseFormGetValues<FormValues>
  /** The counted items of the first page — what the list offers before anything is typed. */
  firstPage: StockItem[]
  /** The line's item, once known — kept in the list whatever the search finds. */
  selected: StockItem | undefined
  /** The items other lines already use — offered, but not choosable twice. */
  takenIds: string[]
  /** Told of each item picked, so the dialog can read its unit, cost and stock. */
  onPick: (item: StockItem) => void
}

/**
 * The item a line adjusts, searched for by name, SKU or barcode — or scanned.
 *
 * Typing asks inventory for matches, keeping the counted ones: only those hold
 * stock. A scanner types its code and presses Enter, so Enter on a code that is
 * exactly one item's barcode or SKU picks that item. Enter in the box never
 * saves the draft, which a scan otherwise would, half-filled.
 */
function ItemField({
  index,
  control,
  getValues,
  firstPage,
  selected,
  takenIds,
  onPick,
}: ItemFieldProps) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [searchText, setSearchText] = useState('')
  /** Why the last code entered picked nothing, said under the box until the next keystroke. */
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  /* Picking an item writes its name into the box, which reads as a search for
     it — not something typed, so the first page comes back on reopening. */
  const term = searchText === selected?.name ? '' : searchText

  const query = useMemo(() => matchQuery(term), [term])
  // Held back with nothing typed: the first page is already here, and the
  // service answers `search=` with a 400.
  const {
    items: matches,
    isLoading,
    isRefreshing,
  } = useStockItems(query, { enabled: Boolean(term) })
  const isSearching = Boolean(term) && (isLoading || isRefreshing)

  const options = useMemo(() => {
    const found = term ? matches.filter(isCounted) : firstPage
    return selected && !found.some((option) => option.id === selected.id)
      ? [selected, ...found]
      : found
  }, [term, matches, firstPage, selected])

  /**
   * A code entered with Enter: the item whose barcode or SKU it is. The list may
   * not have caught up with a scanner — its debounce outlasts the scan — so
   * inventory is asked for the code itself, sharing the list's cache.
   */
  async function lookUp(code: string, choose: (item: StockItem) => void) {
    const from = getValues(`lines.${index}.itemId`)
    let found: StockItem[]
    try {
      const query = matchQuery(code)
      found = (
        await queryClient.fetchQuery({
          queryKey: [...inventoryKey, 'items', query],
          queryFn: () => fetchStockItems(query),
        })
      ).rows
    } catch {
      setNotice('Could not look that code up. Try again.')
      return
    }

    // Enter on a highlighted row picks it; that choice stands over the code.
    if (getValues(`lines.${index}.itemId`) !== from) return

    const hit = found.find((item) => isCodeOf(item, code))
    if (!hit) setNotice(`No item has the code “${code}”.`)
    else if (!isCounted(hit)) setNotice(`${hit.name} is not counted, so it holds no stock.`)
    else if (takenIds.includes(hit.id)) setNotice(`${hit.name} is already on this adjustment.`)
    else choose(hit)
  }

  return (
    <Controller
      control={control}
      name={`lines.${index}.itemId`}
      render={({ field, fieldState }) => {
        const choose = (item: StockItem) => {
          setNotice(null)
          onPick(item)
          field.onChange(item.id)
        }

        return (
          <ComboBox
            value={field.value || null}
            onChange={(key) => {
              const item = options.find((option) => option.id === String(key))
              if (item) choose(item)
              else field.onChange('')
            }}
            onInputChange={(text) => {
              setSearch(text)
              setNotice(null)
            }}
            // Inventory has already filtered by name, SKU and barcode; filtering
            // its answer again by the box's text would drop the code matches.
            defaultFilter={() => true}
            allowsEmptyCollection
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label>Item</Label>
            <ComboBox.InputGroup>
              <Input
                ref={field.ref}
                placeholder="Search or scan…"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  const code = search.trim()
                  if (!code || code === selected?.name) return
                  // A scanner's Enter, or a typed code: matched, never a save.
                  event.preventDefault()
                  void lookUp(code, choose)
                }}
              />
              <ComboBox.Trigger />
            </ComboBox.InputGroup>
            <ComboBox.Popover>
              <ListBox
                disabledKeys={takenIds}
                renderEmptyState={() => (
                  <p className="text-muted px-3 py-2 text-xs">
                    {isSearching
                      ? 'Searching…'
                      : term
                        ? `No counted item matches “${term}”.`
                        : 'No counted items yet.'}
                  </p>
                )}
              >
                {options.map((option) => (
                  // The name alone, since it is what the box shows once chosen.
                  <ListBoxItem key={option.id} id={option.id} textValue={option.name}>
                    <span className="flex flex-col">
                      <span>{option.name}</span>
                      <span className="text-muted text-xs">
                        {[option.sku, option.barcode, option.unitOfMeasure]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </ListBoxItem>
                ))}
              </ListBox>
            </ComboBox.Popover>
            {notice && !fieldState.error ? (
              <p role="alert" className="text-danger text-xs">
                {notice}
              </p>
            ) : (
              <FieldError>{fieldState.error?.message}</FieldError>
            )}
          </ComboBox>
        )
      }}
    />
  )
}

/**
 * `On hand here: 10 kg · after: 7 kg (−3)`. Only a preview: the service applies
 * the change to what is on hand when the document posts.
 */
function onHandLine(
  level: StockLevel | undefined,
  kind: AdjustmentKind,
  qty: number | undefined,
  unit: string,
): string {
  if (!level) return 'On hand here: …'

  const now = `On hand here: ${quantity(level.onHand)} ${unit}`
  if (qty === undefined) return now

  const change = kind === 'wastage' ? -qty : qty
  const after = level.onHand + change

  return `${now} · after: ${quantity(after)} ${unit} (${variance(change)})`
}
